import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../contexts/LanguageContext";
import { Lock } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Tell crawlers and search engines never to index this page
  useEffect(() => {
    const existing = document.querySelector('meta[name="robots"]');
    let added = null;
    if (existing) {
      existing.setAttribute("content", "noindex, nofollow, noarchive");
    } else {
      added = document.createElement("meta");
      added.name = "robots";
      added.content = "noindex, nofollow, noarchive";
      document.head.appendChild(added);
    }
    return () => {
      if (added) document.head.removeChild(added);
    };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      nav("/admin");
    } catch {
      // Always show a generic message — never reveal whether the email
      // exists or whether the password was wrong. This prevents user
      // enumeration attacks against the admin account.
      setError(t.admin.invalid_credentials || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-5" data-testid="admin-login-page">
      <div className="w-full max-w-md border border-slate200 bg-white p-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-ink text-white grid place-items-center"><Lock className="w-4 h-4" /></div>
          <div>
            <div className="heading-serif text-2xl text-ink">{t.admin.login}</div>
          </div>
        </div>
        <form onSubmit={onSubmit} className="grid gap-4" autoComplete="off">
          <div>
            <label className="label-eyebrow block mb-2">{t.admin.email}</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
              className="w-full border border-slate200 px-3 h-10 text-[14px] bg-transparent focus:border-ink outline-none"
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t.admin.password}</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full border border-slate200 px-3 h-10 text-[14px] bg-transparent focus:border-ink outline-none"
              data-testid="admin-login-password"
            />
          </div>
          {error && <div className="text-[13px] text-crimson" data-testid="admin-login-error">{error}</div>}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="bg-ink text-white h-11 text-[12px] uppercase tracking-[0.2em] font-medium hover:bg-ink/90 disabled:opacity-50"
          >
            {busy ? t.common.loading : t.admin.login}
          </button>
        </form>
      </div>
    </div>
  );
}
